import { vec3 } from "gl-matrix";
import { useEffect, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { Picker, selectClippingPlanes, selectPicker } from "features/render";
import { projectPointOntoPlane } from "utils/math";

import { useAddLaser } from "./hooks/useAddLaser";
import { clippingOutlineLaserActions, selectFollowClippingPlane, selectOutlineLasers } from "./outlineLaserSlice";

export function useHandleOutlineLasers() {
    const { planes } = useAppSelector(selectClippingPlanes);
    const outlineLasers = useAppSelector(selectOutlineLasers);
    const isLaserPicker = useAppSelector(selectPicker) === Picker.OutlineLaser;
    const dispatch = useAppDispatch();
    const followClippingPlane = useAppSelector(selectFollowClippingPlane);

    const generationRef = useRef(0);

    const addLaser = useAddLaser();
    const prevPlanes = useRef(planes);
    const first = useRef(true);

    useEffect(() => {
        update();

        async function update() {
            if (planes === prevPlanes.current) {
                return;
            }
            prevPlanes.current = planes;

            if (first.current) {
                //Avoid deleting bookmark lasers on init
                first.current = false;
                return;
            }

            generationRef.current++;
            const generation = generationRef.current;
            //We need a sleep to make sure the pick buffer is ready.
            const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
            await sleep(100);
            if (generation < generationRef.current) {
                return;
            }

            for (const laser of outlineLasers) {
                if (!laser.attachedToPlane) {
                    continue;
                }

                let stillAttached = false;
                for (const plane of planes) {
                    const planeNormal = plane.normalOffset.slice(0, 3) as vec3;
                    const isAligned = laser.zDir && vec3.dot(planeNormal, laser.zDir) > 0.999;
                    if (!isAligned) {
                        continue;
                    }

                    stillAttached = true;

                    if (followClippingPlane) {
                        const planePoint = vec3.scale(vec3.create(), planeNormal, plane.normalOffset[3]);
                        const newPosition = projectPointOntoPlane(laser.laserPosition, planeNormal, planePoint);

                        await addLaser(newPosition, planeNormal, {
                            laserId: laser.id,
                            replaceLaserId: laser.id,
                            measureX: laser.measurementX !== undefined,
                            measureY: laser.measurementY !== undefined,
                            measureZ: false,
                            attachedToPlane: true,
                        });
                        if (generation < generationRef.current) {
                            return;
                        }
                    }

                    break;
                }

                if (!stillAttached) {
                    dispatch(clippingOutlineLaserActions.removeLaser(laser.id));
                }
            }
        }
    }, [dispatch, addLaser, outlineLasers, planes, followClippingPlane]);

    // Reset screen space laser lock when switching away from laser picker
    useEffect(() => {
        if (!isLaserPicker) {
            dispatch(clippingOutlineLaserActions.setLockedRotation(null));
        }
    }, [dispatch, isLaserPicker]);
}
